import { isNullOrUndefined } from '@shared';

interface PolicyExtractionOptions {
  policyType?: PolicyFundingSource['policyType'] | null;
}

export function policyExtractedDataToPolicyState(
  processedResponse: ParsedPolicyDataExtractResponse,
  currentPolicyState: Partial<PolicyFundingSource>,
  options: PolicyExtractionOptions = {},
): PolicyFundingSource {
  const extractedData = {} as Partial<PolicyFundingSource>;

  if (!isNullOrUndefined(options.policyType)) {
    extractedData.policyType = options.policyType;
  }

  const divideByHundred = (value: number) => value / 100;

  const setFieldValue = (
    parsedField: (PolicyExtractedField & { parsedFieldValue: any }) | undefined,
    policyFieldKey: PolicyFieldKey,
    transformValue?: (value: any) => any,
  ) => {
    if (parsedField && !isNullOrUndefined(parsedField.parsedFieldValue)) {
      extractedData[policyFieldKey] = transformValue
        ? transformValue(parsedField.parsedFieldValue)
        : parsedField.parsedFieldValue;
    }
  };

  setFieldValue(
    processedResponse.policyMinimumGuaranteedBenefitAmount,
    'policyMinimumGuaranteedBenefitAmount',
  );
  setFieldValue(processedResponse.policyLumpSumPayment, 'policyLumpSumPayment');
  setFieldValue(
    processedResponse.policyInflationProtection,
    'policyInflationProtection',
    divideByHundred,
  );
  setFieldValue(processedResponse.policyPremiumMonthlyCost, 'policyPremiumMonthlyCost');
  setFieldValue(processedResponse.policyPremiumStartYear, 'policyPremiumStartYear');

  setFieldValue(processedResponse.policyMaximumBenefitAmount, 'policyMaximumBenefitAmount');
  setFieldValue(
    processedResponse.dailyAssistedLivingBenefitAmount,
    'dailyAssistedLivingBenefitAmount',
  );

  setFieldValue(processedResponse.dailyHomeBenefitAmount, 'dailyHomeBenefitAmount');

  setFieldValue(
    processedResponse.dailyIndependentLivingBenefitAmount,
    'dailyIndependentLivingBenefitAmount',
  );

  setFieldValue(processedResponse.dailyNursingHomeBenefitAmount, 'dailyNursingHomeBenefitAmount');
  setFieldValue(processedResponse.monthlyHomeBenefitAmount, 'monthlyHomeBenefitAmount');
  setFieldValue(
    processedResponse.monthlyAssistedLivingBenefitAmount,
    'monthlyAssistedLivingBenefitAmount',
  );
  setFieldValue(
    processedResponse.monthlyIndependentLivingBenefitAmount,
    'monthlyIndependentLivingBenefitAmount',
  );
  setFieldValue(
    processedResponse.monthlyNursingHomeBenefitAmount,
    'monthlyNursingHomeBenefitAmount',
  );
  setFieldValue(processedResponse.homeCareWaitingPeriodDays, 'homeCareWaitingPeriodDays');
  setFieldValue(processedResponse.facilityCareWaitingPeriodDays, 'facilityCareWaitingPeriodDays');
  setFieldValue(processedResponse.policyLimitedPayYears, 'policyLimitedPayYears');
  setFieldValue(processedResponse.policyBenefitPeriodMonths, 'policyBenefitPeriodMonths');
  setFieldValue(
    processedResponse.policyContinuationBenefitAmount,
    'policyContinuationBenefitAmount',
  );
  setFieldValue(
    processedResponse.policyMonthlyWithdrawalRate,
    'policyMonthlyWithdrawalRate',
    divideByHundred,
  );
  setFieldValue(
    processedResponse.policyPremiumIncreaseRate,
    'policyPremiumIncreaseRate',
    divideByHundred,
  );
  setFieldValue(processedResponse.policyPremiumIncreaseYears, 'policyPremiumIncreaseYears');
  setFieldValue(
    processedResponse.policyInflationProtectionOnFaceAmount,
    'policyInflationProtectionOnFaceAmount',
    divideByHundred,
  );
  setFieldValue(
    processedResponse.policyInflationProtectionOnCOBAmount,
    'policyInflationProtectionOnCOBAmount',
    divideByHundred,
  );
  setFieldValue(processedResponse.policyInflationProtectionYears, 'policyInflationProtectionYears');
  setFieldValue(
    processedResponse.policyBaseBenefitAmountAtIssue,
    'policyBaseBenefitAmountAtIssue',
  );
  setFieldValue(
    processedResponse.policyContinuationBenefitAmountAtIssue,
    'policyContinuationBenefitAmountAtIssue',
  );
  setFieldValue(
    processedResponse.policyAccelerationBenefitDurationMonths,
    'policyAccelerationBenefitDurationMonths',
  );
  setFieldValue(
    processedResponse.policyEobBenefitDurationMonths,
    'policyEobBenefitDurationMonths',
  );
  setFieldValue(
    processedResponse.policyBaseBenefitGuaranteedInitialRate,
    'policyBaseBenefitGuaranteedInitialRate',
    divideByHundred,
  );
  setFieldValue(
    processedResponse.policyBaseBenefitGuaranteedInitialRateYears,
    'policyBaseBenefitGuaranteedInitialRateYears',
  );
  setFieldValue(
    processedResponse.policyBaseBenefitGuaranteedRenewalRate,
    'policyBaseBenefitGuaranteedRenewalRate',
    divideByHundred,
  );
  setFieldValue(
    processedResponse.policyMonthlyContinuationBenefitChargeRate,
    'policyMonthlyContinuationBenefitChargeRate',
  );
  setFieldValue(
    processedResponse.policyContinuationBenefitFirstAnniversaryRate,
    'policyContinuationBenefitFirstAnniversaryRate',
    divideByHundred,
  );
  setFieldValue(processedResponse.policyAccumulatedValueAtIssue, 'policyAccumulatedValueAtIssue');
  setFieldValue(
    processedResponse.policyAccumulatedValueGuaranteedInitialRate,
    'policyAccumulatedValueGuaranteedInitialRate',
    divideByHundred,
  );
  setFieldValue(
    processedResponse.policyAccumulatedValueGuaranteedInitialRateYears,
    'policyAccumulatedValueGuaranteedInitialRateYears',
  );
  setFieldValue(
    processedResponse.policyAccumulatedValueGuaranteedRenewalRate,
    'policyAccumulatedValueGuaranteedRenewalRate',
    divideByHundred,
  );
  if (processedResponse.isPrimaryPolicyHolder?.fieldValue) {
    extractedData.isPrimaryPolicyHolder =
      processedResponse.isPrimaryPolicyHolder.fieldValue.toLowerCase() === 'yes';
  }

  if (processedResponse.policyPaymentType?.fieldValue) {
    extractedData.isIndemnityPolicyPayment =
      processedResponse.policyPaymentType.fieldValue.toLowerCase() === 'indemnity';
  }
  setFieldValue(processedResponse.policyIndemnityBenefitPercent, 'policyIndemnityBenefitPercent');

  if (processedResponse.policyInflationProtectionType?.fieldValue) {
    extractedData.isSimpleInflationProtection =
      processedResponse.policyInflationProtectionType.fieldValue.toLowerCase() === 'simple';
  }

  return {
    ...currentPolicyState,
    ...extractedData,
  } as PolicyFundingSource;
}
